/**
 * @brisa/ui · OAuthProviderButton
 *
 * Botão de provider OAuth com hierarquia visual inequívoca.
 *   - primary (Google): Roxo Noite preenchido — caminho dominante (legacy auth).
 *   - secondary (email): outline transparente — alternativa não escondida (legacy auth).
 *   - facelift-pill (W3.2 UI_SPEC §2.6 / Figma 223:797): pill branco com border
 *     `text-16`, h-48px, label Archivo Medium 16px black. Usado no login
 *     redesenhado `/app/auth/login`.
 *
 * data-event="signup-started" data-provider={provider} para tracking.
 */
import * as React from 'react';

export type OAuthProvider = 'google' | 'email';
export type OAuthVariant = 'primary' | 'secondary' | 'facelift-pill';

export interface OAuthProviderButtonProps {
  provider: OAuthProvider;
  variant: OAuthVariant;
  loading?: boolean;
  disabled?: boolean;
  onClick?: () => void;
  label: string;
  loadingLabel?: string;
}

export const OAuthProviderButton = React.forwardRef<
  HTMLButtonElement,
  OAuthProviderButtonProps
>(function OAuthProviderButton(
  { provider, variant, loading = false, disabled = false, onClick, label, loadingLabel },
  ref,
) {
  const isDisabled = disabled || loading;
  const display = loading ? (loadingLabel ?? label) : label;

  if (variant === 'facelift-pill') {
    return (
      <button
        ref={ref}
        type="button"
        onClick={isDisabled ? undefined : onClick}
        disabled={isDisabled}
        aria-disabled={isDisabled || undefined}
        aria-label={label}
        data-event="signup-started"
        data-provider={provider}
        data-variant={variant}
        className={[
          'flex h-12 w-full items-center justify-center gap-2',
          'rounded-full border border-[hsl(var(--text-16))] bg-white px-6',
          // Hover/active scale match primary-large CTA (button.tsx) — leve press feedback,
          // sem trocar bg (decisão PL 2026-05-23 W3.2: consistência entre OAuth pill
          // facelift e CTA amarelo facelift; mudar bg compete com a hierarquia visual).
          'transition-transform duration-fast ease-out',
          'hover:scale-[0.98] active:scale-[0.96]',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
          'disabled:cursor-not-allowed disabled:opacity-50',
        ].join(' ')}
      >
        {provider === 'google' && !loading && <GoogleIconColor />}
        {provider === 'email' && !loading && <MailIconNeutral />}
        {loading && <LoadingDots />}
        <span
          className="font-body text-black"
          style={{ fontSize: '16px', fontWeight: 500 }}
        >
          {display}
        </span>
      </button>
    );
  }

  return (
    <button
      ref={ref}
      type="button"
      onClick={isDisabled ? undefined : onClick}
      disabled={isDisabled}
      aria-disabled={isDisabled || undefined}
      aria-label={label}
      data-event="signup-started"
      data-provider={provider}
      data-variant={variant}
      className={`brisa-oauth-button brisa-oauth-button--${variant}`}
    >
      {provider === 'google' && !loading && <GoogleIcon />}
      {provider === 'email' && !loading && <MailIcon />}
      {loading && <LoadingDots />}
      <span>{display}</span>
    </button>
  );
});

// Google glifo COLORIDO (facelift-pill variant — pill branco), distinto do
// glifo monocromático (currentColor) usado em variant primary/secondary.
function GoogleIconColor() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
      <path
        fill="#4285F4"
        d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.79 2.72v2.26h2.9c1.7-1.57 2.69-3.88 2.69-6.62z"
      />
      <path
        fill="#34A853"
        d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.9-2.26c-.81.54-1.84.86-3.06.86-2.35 0-4.34-1.59-5.05-3.72H.96v2.34A9 9 0 0 0 9 18z"
      />
      <path
        fill="#FBBC05"
        d="M3.95 10.7a5.42 5.42 0 0 1 0-3.4V4.96H.96a9 9 0 0 0 0 8.08l2.99-2.34z"
      />
      <path
        fill="#EA4335"
        d="M9 3.58c1.32 0 2.5.45 3.44 1.34l2.58-2.58A9 9 0 0 0 9 0 9 9 0 0 0 .96 4.96L3.95 7.3C4.66 5.17 6.65 3.58 9 3.58z"
      />
    </svg>
  );
}

function MailIconNeutral() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true" fill="none">
      <rect x="3" y="5" width="18" height="14" rx="2" stroke="currentColor" strokeWidth="1.5" />
      <path d="m3 7 9 6 9-6" stroke="currentColor" strokeWidth="1.5" />
    </svg>
  );
}

function GoogleIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" aria-hidden="true">
      <path
        fill="currentColor"
        d="M19.6 10.23c0-.68-.06-1.34-.18-1.97H10v3.72h5.39c-.23 1.24-.94 2.29-2 2.99v2.49h3.23c1.89-1.74 2.98-4.31 2.98-7.23z"
      />
      <path
        fill="currentColor"
        d="M10 20c2.7 0 4.96-.9 6.62-2.43l-3.23-2.5c-.9.6-2.04.96-3.39.96-2.6 0-4.81-1.76-5.6-4.13H1.06v2.6A10 10 0 0 0 10 20z"
        opacity="0.7"
      />
      <path
        fill="currentColor"
        d="M4.4 11.9a6 6 0 0 1 0-3.8V5.5H1.06a10 10 0 0 0 0 9l3.34-2.6z"
        opacity="0.5"
      />
      <path
        fill="currentColor"
        d="M10 3.96c1.47 0 2.79.5 3.83 1.5l2.87-2.87C15 .9 12.7 0 10 0a10 10 0 0 0-8.94 5.5l3.34 2.6c.79-2.37 3-4.13 5.6-4.13z"
        opacity="0.4"
      />
    </svg>
  );
}

function MailIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" aria-hidden="true" fill="none">
      <rect x="2" y="4" width="16" height="12" rx="2" stroke="currentColor" strokeWidth="1.5" />
      <path d="m2 6 8 6 8-6" stroke="currentColor" strokeWidth="1.5" />
    </svg>
  );
}

function LoadingDots() {
  return (
    <span className="brisa-oauth-button__dots" aria-hidden="true">
      <span /> <span /> <span />
    </span>
  );
}
